/*
 * Round 15 (2026-09-30), Varun item 7: what the saved-collection card of the Slot Stats page shows.
 *
 * SERVER CONTRACT (r15c/server @ 41d81aa, SavedCollectionStats.View.asMap, UiApiSlotStatsController):
 *   GET /uiapi/slots/{slot}/saved-collection ->
 *     { slotName, enabled (697), collectionName (698, "" when blank), linksPerSession (699 in effect: NULL = 3,
 *       clamped 1..10), removeAfterQueued (700, only while consume mode is available), consumeAvailable,
 *       settings: { "697", "698", "699" (as saved, null = never), "699Effective", "700" (only while consume mode
 *       is available) },
 *       totals: { read, queued, posted, failed, removed (only while consume mode is available or a removal was
 *                 recorded) }                       - null = "not recorded", never 0
 *       lastReadAt ("yyyy-MM-dd HH:mm", slot time), lastReport ("yyyy-MM-dd HH:mm (<zone>) '<698>': read N,
 *       queued N, posted N, failed N[, removed N]; <note>; <note>..."), blockers [..] (what stops the step, as far
 *       as the server can see - V1's own wording), summary }
 *     404 {error:'not_enabled'} while -Dnecro.server.savedCollection is OFF (the default until the live run
 *     passes - O8); a server before round 15 has no such route (404 / 405 without a code).
 *   No day-by-day history: the server keeps running totals only (IGAccStats savedCollection*Total), so the card
 *   has no chart.
 *
 * The worker's notes in the report are its reasons, verbatim: "collection not found", "wrong account", "not due:
 * the slot posts from a fixed folder (677 / 4kStogram): collection posts need 677 off", "not due: 691 on ...",
 * "not due: slot keyboard setting off ...", "623 is 0: nothing queued", "not due: next visit after ... (last
 * outcome: ...)" (BC r15c R15SavedCollection.notDueReason / SR2 r15ColNotes). They are listed, not reworded.
 */
import { num, splitStamp } from './closeFriendsView';

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
const str = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());

/** The /saved-collection body -> what the card reads, or null when it is not that body (the card is not drawn). */
export function normaliseSavedCollection(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (!has(raw, 'enabled') && !has(raw, 'totals')) return null;
  const totalsRaw = raw.totals && typeof raw.totals === 'object' ? raw.totals : {};
  const settings = raw.settings && typeof raw.settings === 'object' ? raw.settings : {};
  const removeAfterQueued = raw.removeAfterQueued === true;
  const removedSent = has(totalsRaw, 'removed');
  const removed = removedSent ? num(totalsRaw.removed) : null;
  return {
    slotName: str(raw.slotName),
    enabled: raw.enabled === true,
    collectionName: str(raw.collectionName) || '',
    linksPerSession: num(raw.linksPerSession),
    linksPerSessionSaved: has(settings, '699') ? num(settings['699']) : null,
    consumeAvailable: raw.consumeAvailable === true,
    removeAfterQueued,
    totals: {
      read: num(totalsRaw.read),
      queued: num(totalsRaw.queued),
      posted: num(totalsRaw.posted),
      failed: num(totalsRaw.failed),
      removed,
    },
    // "removed" only when consume mode (700) is on for this slot, or a removal was recorded - the rule of the
    // server's own summary line - and only when the server sent the total at all
    showRemoved: removedSent && (removeAfterQueued || (removed !== null && removed > 0)),
    lastReadAt: str(raw.lastReadAt),
    lastReport: str(raw.lastReport),
    blockers: Array.isArray(raw.blockers) ? raw.blockers.map(str).filter(Boolean) : [],
    summary: str(raw.summary),
  };
}

/** Any total recorded (the off line then says what was done before). */
export function anyTotal(sc) {
  if (!sc) return false;
  const t = sc.totals;
  return [t.read, t.queued, t.posted, t.failed].some((v) => v !== null) || (sc.showRemoved && t.removed !== null);
}

/**
 * The stored report -> { at, zone, counts, notes[] }: counts is the worker's "'<name>': read N, queued N, ..."
 * part, notes its reasons, in order. A line in another shape comes back as one note.
 */
export function splitCollectionReport(report) {
  const r = splitStamp(report);
  if (!r) return null;
  const parts = r.text.split(/;\s+/).map((p) => p.trim()).filter(Boolean);
  let counts = null;
  if (parts.length && /^'[\s\S]*':/.test(parts[0])) counts = parts.shift();
  return { at: r.at, zone: r.zone, counts, notes: parts };
}
